import React from 'react';
import { FaThumbsUp, FaThumbsDown, FaCopy } from 'react-icons/fa';

type CallFeedbackAPI = (
  proxyBase: string,
  messageId: string,
  sessionId: string,
  impression: string,
  feedbackText: string,
) => Promise<void>;

// Like / dislike / copy row rendered under the latest bot message. Opens a small
// feedback dialog and posts the impression + optional comment via callFeedbackAPI.
export const ActionButtons = ({ callFeedbackAPI, proxyBase, sessionId, messageId, lastBotMessage }: {
  callFeedbackAPI: CallFeedbackAPI,
  proxyBase: string,
  sessionId: string,
  messageId: string,
  lastBotMessage: string
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [feedbackSent, setFeedbackSent] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [selectedImpression, setSelectedImpression] = React.useState<"AddLike" | "AddDislike" | null>(null);


  React.useEffect(() => {
    // Hide all previous ActionButtons instances when this one mounts
    document.querySelectorAll('.rcb-action-buttons').forEach((el) => {
      if (el !== ref.current) {
        (el as HTMLElement).style.display = 'none';
      }
    });

    // Show this one after a short delay
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    if (selectedImpression) {
      callFeedbackAPI(proxyBase, messageId, sessionId, selectedImpression, feedbackText);
    }
    setDialogOpen(false);
    setFeedbackText("");
    setFeedbackSent(true);
    setTimeout(() => setFeedbackSent(false), 1500); // auto-dismiss after 1.5s
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(lastBotMessage).catch((err) => {
      console.error("Failed to copy text: ", err);
    });
  }

  if (!visible) return null;
  return (
    <>
    <style>
      {`
        .rcb-action-buttons button {
          transition: transform 0.15s ease, color 0.15s ease;
        }
        .rcb-action-buttons button[title="Like"]:hover {
          transform: scale(1.25);
          color: #FFCD11 !important;
        }
        .rcb-action-buttons button[title="Dislike"]:hover {
          transform: scale(1.25);
          color: #FFCD11 !important;
        }
        .rcb-action-buttons button[title="Copy"]:hover {
          transform: scale(1.25);
          color: #FFCD11 !important;
        }


      `}
    </style>
    <div ref={ref} className="rcb-action-buttons" style={{ display: "flex", gap: "8px", marginBottom: "5px", marginLeft: "20px" }}>
      <button title="Copy" style={{ background: "none", border: "none", cursor: "pointer", color: "#434343", paddingTop: "2px" }}
       onClick={handleCopy}><FaCopy /></button>

      <button title="Like" style={{ background: "none", border: "none", cursor: "pointer", color: "#434343" }}
      onClick={() => { setSelectedImpression("AddLike"); setDialogOpen(true); }}><FaThumbsUp /></button>

      <button title="Dislike" style={{ background: "none", border: "none", cursor: "pointer", color: "#434343", paddingTop: "2px" }}
      onClick={() => { setSelectedImpression("AddDislike"); setDialogOpen(true); }}><FaThumbsDown /></button>

      {dialogOpen && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
        }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '360px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Thanks for the feedback!</h3>
          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>Add a comment (optional)</p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Tell us more..."
              style={{ width: '100%', height: '80px', borderRadius: '4px', border: '1px solid #ccc', padding: '8px', fontSize: '14px', resize: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setDialogOpen(false)}
                style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSubmit}
                style={{ padding: '6px 16px', borderRadius: '4px', border: 'none', background: '#FFCD11', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS — Feedback Sent */}
      {feedbackSent && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
        }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '300px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '48px', color: '#FFCD11' }}>✔</div>
            <h3 style={{ margin: '8px 0 0', fontSize: '16px', color: '#000' }}>Thanks for your feedback!</h3>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
